import { NavLink, Outlet } from 'react-router-dom';
import { Cog, Plug, ShieldCheck } from 'lucide-react';
import type { UiFeature } from '@repo/shared-types';
import { useAuth } from '../../lib/auth';
import { useCurrentProject } from '../../lib/project';
import { cn } from '../../lib/utils';

/**
 * Shell for everything that configures one project: General, Integrations, Access.
 *
 * Integrations and Access used to be flat entries in the sidebar's Settings group, where — like
 * Knowledge base and Settings before them — they silently meant "the project you happen to be
 * in" (see the RowMenu note in ProjectNavTree). They now sit one level under the project's own
 * Settings, reached from the tree row that names their subject, so the project is chosen before
 * the setting is.
 *
 * A layout route rather than a header each page renders itself: the tab strip has to survive
 * moving between the three, and the project is resolved once here instead of three times.
 */
interface Tab {
  /** Segment under `/p/:slug/settings`; empty for the index tab. */
  path: string;
  label: string;
  icon: typeof Cog;
  /**
   * The UI feature gating this tab, matching the route's own guard. General has none, for the
   * same reason the settings route is ungated: it is where a project is renamed, moved and
   * deleted, so a flag could strand a project with no way to manage it.
   */
  feature?: UiFeature;
}

const TABS: Tab[] = [
  { path: '', label: 'General', icon: Cog },
  { path: 'integrations', label: 'Integrations', icon: Plug, feature: 'INTEGRATIONS' },
  { path: 'access', label: 'Access', icon: ShieldCheck, feature: 'ACCESS' },
];

export function ProjectSettingsLayout() {
  const { project, projectSlug, loading } = useCurrentProject();
  const { hasFeature } = useAuth();

  const base = `/p/${projectSlug}/settings`;
  const tabs = TABS.filter((tab) => !tab.feature || hasFeature(tab.feature));

  return (
    <div className="flex h-screen overflow-hidden bg-surface">

      <div className="pl-[var(--sidebar-w)] pr-[var(--rail-w)] flex-1 h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto px-7 py-7">
          <div className="mb-5">
            <h1 className="text-lg font-bold text-ink">Settings</h1>
            <p className="text-xs text-ink-muted mt-1 font-mono">{project?.path ?? '…'}</p>
          </div>

          {/* Absolute hrefs rather than relative ones: the index tab and its children share a
              prefix, and `end` on a relative "." is the one combination that reads ambiguously. */}
          <nav className="flex items-center gap-1 border-b border-border mb-6">
            {tabs.map((tab) => (
              <NavLink
                key={tab.path}
                to={tab.path ? `${base}/${tab.path}` : base}
                end={tab.path === ''}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 px-3 py-2 -mb-px text-sm font-medium border-b-2 transition-colors',
                    isActive
                      ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                      : 'border-transparent text-ink-muted hover:text-ink',
                  )
                }
              >
                <tab.icon size={14} />
                {tab.label}
              </NavLink>
            ))}
          </nav>

          {loading && !project ? <p className="text-xs text-ink-faint">Loading…</p> : <Outlet />}
        </div>
      </div>
    </div>
  );
}
